import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { userPool } from './cognito';

type AuthContextValue = {
  loading: boolean;
  session: CognitoUserSession | null;
  token: string;
  setSession: (session: CognitoUserSession | null) => void;
  logout: () => void;
  refresh: () => Promise<CognitoUserSession>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<CognitoUserSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) { queueMicrotask(() => setLoading(false)); return; }
    currentUser.getSession((error: Error | null, currentSession: CognitoUserSession | null) => {
      if (!error && currentSession?.isValid()) setSession(currentSession);
      setLoading(false);
    });
  }, []);

  const logout = useCallback(() => { userPool.getCurrentUser()?.signOut(); setSession(null); }, []);
  const refresh = useCallback(() => new Promise<CognitoUserSession>((resolve, reject) => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) return reject(new Error('No hay una sesión activa.'));
    currentUser.getSession((sessionError: Error | null, currentSession: CognitoUserSession | null) => {
      if (sessionError || !currentSession) return reject(sessionError ?? new Error('No pudimos leer la sesión.'));
      currentUser.refreshSession(currentSession.getRefreshToken(), (refreshError, refreshedSession) => {
        if (refreshError || !refreshedSession) return reject(refreshError ?? new Error('No pudimos actualizar la sesión.'));
        setSession(refreshedSession); resolve(refreshedSession);
      });
    });
  }), []);
  return <AuthContext.Provider value={{ loading, session, token: session?.getAccessToken().getJwtToken() ?? '', setSession, logout, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider.');
  return context;
}
